/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Input, Select, Switch } from "core/components";
import { navigateTo, navigateToIf } from "core/navigation";
import { Department, RoleFormData, UserFormData, UserSchema } from "core/schema";
import { Option } from "core/types";
import { admin_dashboard_index_path, admin_users_path, admin_user_path, new_user_session_path } from "routes";
import ChangePasswordForm from "./ChangePasswordForm";

type UserFormProps = {
  user: UserFormData;
  roles: Array<RoleFormData>;
  departments: Array<Option>;
  policy?: Record<string, boolean>;
};

const UserForm = ({ user, roles, departments, warehouses, current_user }: UserFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors }
  } = useForm<UserFormData>({
    mode: "onChange",
    defaultValues: UserSchema.cast(user),
    resolver: yupResolver(UserSchema)
  });
  const department = watch("department");
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [showChangePasswordModal, setShowChangePasswordModal] = React.useState(false);

  const save = async (data: UserFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_user_path(user), data);
      } else {
        await api.post(admin_users_path(user), data);
      }

      navigateToIf(user.read_only || current_user.id == user.id, admin_dashboard_index_path(), admin_users_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={user.id ? "Edit User" : "New User"}>
            {!(user.read_only || current_user.id == user.id) && (
              <>
                <Button
                  icon="arrow-left"
                  iconProps={{ className: "mr-3" }}
                  className="button--default mr-3"
                  disabled={isSubmitting}
                  title="Back"
                  onClick={() => navigateTo(admin_users_path())}
                />
                <Button
                  icon="save"
                  iconProps={{ className: "mr-3" }}
                  className="button--brand mr-3"
                  disabled={isSubmitting}
                  title="Save"
                  onClick={handleSubmit(async (data) => await save(data))}
                />
              </>
            )}
            {!user.read_only && current_user.id == user.id && (
              <Button
                className="button--brand mr-3"
                disabled={isSubmitting}
                title="Change Password"
                onClick={() => {
                  setShowChangePasswordModal(!showChangePasswordModal);
                }}
              />
            )}
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                        disabled={user.read_only || current_user.id == user.id}
                      />
                    )}
                  />
                  <Controller
                    name="department"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Department"
                        placeholder="Select Department"
                        required
                        options={departments}
                        isOptionDisabled={(option) => option.value == "system_administration"}
                        onChange={(newOption) => {
                          field.onChange(newOption?.value);
                          setValue("role_id", null);
                          setValue("warehouse_id", null);
                          setValue("warehouse_users_attributes", []);
                        }}
                        value={departments.find((department) => department.value == field.value)}
                        isDisabled={user.read_only || current_user.id == user.id}
                        errorMessage={errors.department?.message}
                      />
                    )}
                  />
                  <Controller
                    name="role_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Role"
                        placeholder="Select Role"
                        required
                        noOptionsMessage={() => "Select a Department first."}
                        options={roles.filter((role) => role.department == department)}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={roles.find((role) => role.id == field.value) || null}
                        errorMessage={errors.role_id?.message}
                        disabled={user.read_only || current_user.id == user.id}
                      />
                    )}
                  />
                  {department == Department.Logistics && (
                    <Controller
                      name="warehouse_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label="Designated Warehouse"
                          placeholder={user.read_only ? "All Warehouses" : "Select Warehouse"}
                          required
                          options={warehouses}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          isOptionDisabled={(option) => !option.active}
                          onChange={(newOption) => {
                            field.onChange(newOption?.id);

                            if (newOption?.id) {
                              setValue("warehouse_users_attributes", [{ warehouse_id: newOption?.id }]);
                            }
                          }}
                          value={warehouses.find((warehouse) => warehouse.id == field.value)}
                          isDisabled={user.read_only || current_user.id == user.id}
                          errorMessage={errors.warehouse_id?.message}
                        />
                      )}
                    />
                  )}
                  <Input
                    {...register("email")}
                    label="Email Address"
                    info="This should be a valid working email address because the login credentials will be sent to this email."
                    required
                    errorMessage={errors.email?.message}
                    maxLength={100}
                    readOnly={user.read_only || user.id}
                  />
                  <Input
                    {...register("first_name")}
                    label="First Name"
                    required
                    errorMessage={errors.first_name?.message}
                    maxLength={100}
                    readOnly={user.read_only}
                  />
                  <Input
                    {...register("last_name")}
                    label="Last Name"
                    required
                    errorMessage={errors.last_name?.message}
                    maxLength={100}
                    readOnly={user.read_only}
                  />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
      {user.id && (
        <ChangePasswordForm
          user={user}
          show={showChangePasswordModal}
          onCancel={() => setShowChangePasswordModal(!showChangePasswordModal)}
          onSubmit={() => {
            setShowChangePasswordModal(!showChangePasswordModal);
            if (current_user.id == user.id) {
              navigateTo(new_user_session_path());
            } else {
              navigateTo(admin_users_path());
            }
          }}
        />
      )}
    </React.StrictMode>
  );
};

export default UserForm;
